import axios, { AxiosResponse } from 'axios';

enum Estatus {
  upload = 0,
  status,
  result,
}

enum EuploadStatus {
  normal = 0,
  fail,
  overSize,
}

interface ApiResponse {
  applied: boolean;
  error_msg: string;
}

interface StatisticResponse extends ApiResponse {
  cat_num: number;
  issuer_num: number;
  issued_cnt: number;
  verify_cnt: number;
}

interface BulletinResponse extends ApiResponse {
  count?: number;
  list?: { bid: number; date: string; title: string; tag: string }[];
  content?: { bid: number; date: string; title: string; tag: string; text: string };
}

interface DocumentResponse extends ApiResponse {
  count?: number;
  list?: { did: number; date: string; title: string; link: string; file_size?: number | null }[];
}

interface Metadata {
  chinese_name: string;
  student_id: string;
  graduate_univ: string;
  graduate_colg: string;
  graduate_dept: string;
  AATL: string;
}

interface VersionInfo {
  cur_ver: number;
  num_ver: number;
}

interface AugmentedMetadata {
  chinese_name: string;
  e_chinese_name: string;
  birth_date: string;
  student_id: string;
  graduate_univ: string;
  e_graduate_univ: string;
  graduate_colg: string;
  e_graduate_colg: string;
  graduate_dept: string;
  e_graduate_dept: string;
  degree: string;
  e_degree: string;
  cert_num: string;
  e_cert_num: string;
  graduate_year_month: string;
  minor_d_major: string;
  e_minor_d_major: string;
  reissue_date: string;
  reissue_cert_num: string;
  nationality: string;
  e_graduate_acad: string;
  original_class: string;
  identify_id: string;
}

interface VcMetadataResponse extends ApiResponse {
  augmented_metadata?: AugmentedMetadata;
  identifier?: string;
}

interface VcQrcodeResponse extends ApiResponse {
  qr_code?: string;
}

interface VcCountResponse {
  Chinese: Record<string, number>;
  English: Record<string, number>;
}

const MAIN_HOST = (window as any).env.MAIN_HOST;
const DCMC_HOST = (window as any).env.DCMC_HOST;

/**
 * Fetches an authentication token from the server.
 *
 * @param setToken - Callback function to set the token once fetched.
 */
export const getToken = async (setToken: (token: string) => void): Promise<void> => {
  try {
    const response = await fetch(`${MAIN_HOST}/api/token`, {
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    });
    const token = await response.text();
    setToken(token);
  } catch (err) {
    console.error(err);
  }
};

/**
 * Fetches a captcha from the server.
 *
 * @param setCaptchaID - Callback function to set the captcha ID once fetched.
 */
export const getCaptcha = async (setCaptchaID: (ID: string) => void): Promise<void> => {
  try {
    const response = await fetch(`${MAIN_HOST}/api/captcha/`, {
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    });
    const captcha = await response.text();
    //console.log('got captcha ID:' + captcha)
    if (captcha !== 'DISABLED' && captcha !== 'EXCEPTION_RAISED') setCaptchaID(captcha);
  } catch (err) {
    console.error(err);
  }
};

/**
 * Submits a file to the server for validation.
 *
 * @param file - The file to be uploaded.
 * @param token - The authentication token.
 * @param captchaID - Captcha ID.
 * @param captchaChallenge - Captcha challenge(answer).
 * @param setLoading - Callback function to set the loading state.
 * @param setStatus - Callback function to set the status.
 * @param setResult - Callback function to set the result.
 * @param setUploadStatus - Callback function to set the upload status.
 */
export const submitFile = async (
  file: File | undefined,
  token: string,
  captchaID: string,
  captchaChallenge: string,
  setLoading: (result: boolean) => void,
  setStatus: (status: Estatus) => void,
  setResult: (result: number) => void,
  setMetadata: (result: Metadata) => void,
  setVersionInfo: (result: VersionInfo) => void,
  setUploadStatus: (status: EuploadStatus) => void,
): Promise<void> => {
  if (!file) {
    setUploadStatus(EuploadStatus.fail);
    setLoading(false);
    return;
  } else if (file.size / 1048576 > 2) {
    setUploadStatus(EuploadStatus.overSize);
    setLoading(false);
    return;
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('ID', captchaID);
  formData.append('challenge', captchaChallenge);

  try {
    const response = await fetch(`${MAIN_HOST}/certs/validate/`, {
      body: formData,
      mode: 'cors',
      method: 'POST',
      headers: {
        Authorization: `jwt ${token}`,
        'Access-Control-Allow-Origin': '*',
      },
      credentials: 'include',
    });
    const res = await response.json();

    const result2num = ['success', 'expired', 'failure', 'captchaError', 'JWTExpired'];

    if (!res || !res.error_msg) {
      setStatus(Estatus.upload);
    } else {
      setResult(result2num.indexOf(res.error_msg));
      if (res.metadata)
        setMetadata({
          chinese_name: res.metadata.chinese_name !== undefined ? res.metadata.chinese_name : '',
          student_id: res.metadata.student_id !== undefined ? res.metadata.student_id : '',
          graduate_univ: res.metadata.graduate_univ !== undefined ? res.metadata.graduate_univ : '',
          graduate_colg: res.metadata.graduate_colg !== undefined ? res.metadata.graduate_colg : '',
          graduate_dept: res.metadata.graduate_dept !== undefined ? res.metadata.graduate_dept : '',
          AATL: res.metadata.AATL !== undefined ? res.metadata.AATL : '',
        });
      else
        setMetadata({
          chinese_name: '',
          student_id: '',
          graduate_univ: '',
          graduate_colg: '',
          graduate_dept: '',
          AATL: '',
        });
      if (res.cur_ver && res.num_ver)
        setVersionInfo({
          cur_ver: res.cur_ver,
          num_ver: res.num_ver,
        });
      setStatus(Estatus.result);
    }
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};

/**
 * Fetches a summary of student statistics from the server.
 *
 * @returns StatisticResponse if the response data matches the StatisticResponse type, otherwise undefined.
 */
export const getSummary = async (): Promise<StatisticResponse | undefined> => {
  const url = `${MAIN_HOST}/certs/summary/`;
  function isStatisticData(data: any): data is StatisticResponse {
    return data.applied !== undefined && data.error_msg !== undefined;
  }

  try {
    const response: AxiosResponse = await axios.get(url);

    if (isStatisticData(response.data)) {
      return response.data;
    }
  } catch (error) {
    console.error(error);
  }
};

function isBulletinData(data: any): data is BulletinResponse {
  return data.applied !== undefined && data.error_msg !== undefined;
}

/**
 * Get bulletin data from server.
 *
 * @param bid - When op is 2, specify the number of the detailed content of the bulletin to be returned
 * @param op - Operation mode (1: Returns a list of bulletins without content;
 * 2: Returns all fields of a specific bulletin, including content)
 *
 * @returns BulletinData if the response data matches the BulletinData type, otherwise undefined
 */
export const getBulletin = async (bid: number, op: number = 2): Promise<BulletinResponse | undefined> => {
  const url = `${MAIN_HOST}/certs/bulletin/`;
  const params = { bid, op };

  try {
    const response: AxiosResponse = await axios.get(url, { params });

    if (isBulletinData(response.data)) {
      return response.data;
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * Get bulletin list from server.
 *
 * @param cnt - When op is 1, specify the length of the returned bulletin list.
 * If it is 0, the list of all bulletins is returned.
 * The list is sorted by date, with the newest at the front
 * @param op - Operation mode (1: Returns a list of bulletins without content;
 * 2: Returns all fields of a specific bulletin, including content)
 *
 * @returns BulletinData if the response data matches the BulletinData type, otherwise undefined
 */
export const getBulletinList = async (cnt: number, op: number = 1): Promise<BulletinResponse | undefined> => {
  const url = `${MAIN_HOST}/certs/bulletin/`;
  const params = { cnt, op };

  try {
    const response: AxiosResponse = await axios.get(url, { params });

    if (isBulletinData(response.data)) {
      return response.data;
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * Get document data from server.
 *
 * @param cnt - Specifies the length of the document list to return.
 * If it is 0, the list of all documents is returned.
 * The list is sorted by date, with the newest at the front
 *
 * @returns DocumentResponse if the response data matches the DocumentResponse type, otherwise undefined
 */
export const getDocument = async (cnt: number): Promise<DocumentResponse | undefined> => {
  const url = `${MAIN_HOST}/certs/document/`;
  const params = { cnt };
  function isDocumentResponse(data: any): data is DocumentResponse {
    return data.applied !== undefined && data.error_msg !== undefined;
  }

  try {
    const response: AxiosResponse = await axios.get(url, { params });

    if (isDocumentResponse(response.data)) {
      return response.data;
    }
  } catch (error) {
    console.error(error);
  }
};

export const downloadDocument = async (did: number) => {
  const response = await axios.get(`${MAIN_HOST}/certs/document/${did}/download/`, { responseType: 'blob' });
  return response.data;
};

/**
 * Fetches homepage statistics for display.
 * Returns cert issuance unit count (live from DB) plus
 * vc issuance / verification unit counts (manually configured via backstage).
 *
 * @returns Object containing cert_issuance_units, vc_issuance_units, vc_verification_units
 */
export const getHomeStatConfig = async () => {
  const url = `${MAIN_HOST}/certs/home-stat/`;
  try {
    const response: AxiosResponse = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Submits a PDF file and a code to retrieve augmented VC metadata.
 *
 * @param file - The PDF file to be uploaded.
 * @param code - The verification code.
 * @returns VcMetadataResponse if the request succeeds, otherwise undefined.
 */
export const getVcMetadata = async (file: File, code: string): Promise<VcMetadataResponse | undefined> => {
  const url = `${DCMC_HOST}/api_dcmc/vc_metadata`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('code', code);

  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as VcMetadataResponse;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Fetches a QR code image (base64) for a given VC identifier.
 *
 * @param identifier - The VC identifier string returned from getVcMetadata.
 * @returns VcQrcodeResponse if the request succeeds, otherwise undefined.
 */
export const getVcQrcode = async (identifier: string): Promise<VcQrcodeResponse | undefined> => {
  const url = `${DCMC_HOST}/api_dcmc/vc_qrcode`;
  const formData = new FormData();
  formData.append('identifier', identifier);

  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as VcQrcodeResponse;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Fetches the count of issued VCs grouped by issuing school.
 *
 * @returns VcCountResponse if the request succeeds, otherwise undefined.
 */
export const getVcCount = async (): Promise<VcCountResponse | undefined> => {
  const url = `${DCMC_HOST}/api_dcmc/vc_count`;

  try {
    const response: AxiosResponse = await axios.get(url);
    return response.data as VcCountResponse;
  } catch (error) {
    console.error(error);
  }
};
